// Features: M004-F0736, ML001-F0235, ML001-F0117, ML001-F0119, ML001-F0122, ML001-F0123, M004-F0708, M004-F0709, M004-F0710, M004-F0711, M004-F0712, M004-F0716, M004-F0715, M004-F0720, M004-F0722, M004-F0723
// Tests: T-M004-0271, T-M004-0272, T-M004-0273, T-M004-0274, T-LED-NODEUI-011, T-LED-NODEUI-013, T-LED-WEB-006, T-M004-0194, T-M004-0197, T-M004-0198, T-M004-0199, T-M004-0200, T-M004-0203, T-M004-0204, T-M004-0212, T-M004-0210, T-M004-0225, T-M004-0229, T-M004-0230
//
// The "Advice" and "Approval — next invoice" sections of the customer ledger
// page (Ledger #62, ML001-D013 §2.44, owner-approved mock v3). Rendered by
// LedgerAccountPage directly above "Recent transactions", manager view only
// (§2.44.12: a plain ledger_read member never sees a draft or an advice item).
//
// Capability gate: while the reads are in flight, and whenever Core answers
// them LEDGER_ACCOUNT_OPERATION_UNSUPPORTED (the contract's declared code) or a generic 404,
// this renders NOTHING, so the page stays exactly the baseline. Any other
// failure, including a 404 (node not linked / tunnel down), is shown.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import StepUpDialog from './auth/StepUpDialog';
import TxChip from './TxChip';
import useLedgerMoves from './ledgerMoves/useLedgerMoves';
import StageLine from './ledgerMoves/StageLine';
import ReasonDialog from './ledgerMoves/ReasonDialog';
import CalendarUpdateSection from './ledger/calendarUpdate/CalendarUpdateSection';
import { releaseOutcome } from './ledgerMoves/releaseOutcome';
import { AdviceMoveActions, AdviceMoveForms, DraftLineBackToAdvice, MoveStatus, ProblemBillByHand, ProblemBillByHandForm } from './ledgerMoves/MoveControls';
import { displayTxId, sameHandOff } from '../utils/transactionId';
import TxHistoryDrawer from './TxHistoryDrawer';
import ItemDetails from './ledgerMoves/ItemDetails';
import { adviceMonthSummary, readAdviceCardOpen, writeAdviceCardOpen } from './ledgerAdviceSummary';
import { stageLabel } from './tx/txStages';
import { amountRefusal, DEFAULT_RETRY_SECONDS, PENDING_CODE, isTunnelTimeout, pendingAnswer } from '../services/ledgerMovesAdapter';
import { ledgerAccountApi } from '../services/api';
import { customerBillingProfileApi } from '../services/customerBillingProfileApi';
import DraftGroupSwitcher from './ledger/DraftGroupSwitcher';
import { SELECTOR_SENTENCE, draftsOf, isSelectorRequired, selectorFor, shareText } from './ledger/draftGroups';
import { getStepUpChallengeFromError } from '../utils/stepUp';
import { classifyRelayOutcome } from '../services/relayOutcome';
import AdviceStatementPanel from './ledger/AdviceStatementPanel';
import { isAdviceStatementPanelEnabled } from '../utils/txFeatureFlags';
import { normaliseRelease, RELEASE_IDEMPOTENCY_CONFLICT, RELEASE_NOT_ELIGIBLE, RELEASE_NOT_FOUND, errorCode, formatDay, feedNotBoundMessage, isFeedNotBoundError, isForbiddenError, isPermissionDeniedError, isRouteNotFoundError, isUnsupportedError, normaliseAdvice, normaliseDraft, normaliseIssued, normaliseProblems, normalisePreview, isDraftNotFoundError, PREVIEW_MAX_BYTES, releaseRefusalReason } from '../services/ledgerQueuesAdapter';

// §2.44.4 (Ledger !160 head 58907824): Advice shows only costs NOT on the draft.
// Owner ruling 2026-09-30 23:20Z: a cost lands on the draft by itself when it settles.
const PILL = { coming: 'Coming', being_checked: 'Being checked', deferred: 'Deferred', rejected: 'Rejected' };
// Shown when the server sends no note of its own for the item.
const DEFAULT_NOTE = { deferred: 'removed from the invoice', rejected: 'will not be billed' };
// Refusals the section explains in its own words, then reloads so the view is current.
const KNOWN_REFUSALS = {
  // M004-F0799: a customer with more than one open draft and no selector. The page re-reads draft.list and shows the switcher (see load).
  LEDGER_DRAFT_SELECTOR_REQUIRED: SELECTOR_SENTENCE,
  // M004-F0716 (design 5.2 row 8): says when it BECOMES ready, never that it will land by itself.
  LEDGER_ADVICE_NOT_READY: 'This cost is not ready to bill yet. It becomes ready when the booking settles.',
  LEDGER_DRAFT_CHANGED: 'The invoice changed while you were looking at it. Check the updated lines and total, then approve again.',
  LEDGER_DRAFT_LINE_NOT_FOUND: 'That line is no longer on the invoice. The view has been refreshed.',
  LEDGER_ADVICE_NOT_FOUND: 'That cost is no longer in Advice. The view has been refreshed.',
  // Spec core-ledger-account-gateway/v1 1.1.0 (Spec !72): declared 422 on
  // draft.line.edit (add_manual) and draft.approve when the organisation has
  // no default VAT rate configured. Every other refusal relies on Core's own
  // `message`; this one gets its own copy because "refused" alone would not
  // tell the manager what to do next.
  LEDGER_VAT_REQUIRED: 'A VAT rate is needed for this organisation before this can be added. Ask an admin to set the organisation’s VAT rate, then try again.',
  // Spec core-ledger-account-gateway 1.28.0 (ML001-F0341, Ledger T-LED-APPT-034): draft.approve is refused when the apportioned part lines no longer sum to the hand-off total.
  LEDGER_APPORTIONMENT_SUM_MISMATCH: "The split parts don't add up to the booking price; check the bill-part rules and try again.",
};
// ML001-F0122, ML001-F0123: advice.release refusals (Spec core-ledger-account-gateway 1.2.0), in the manager's words.
// M004-F0716 (design 5.2 rows 1, 5, 9, 10): none of these may state or imply a stage the cost is not in. Where the
// TX id is known the sentence points at it ("(TX-xxxxxxxx)"); the history drawer (M004-F0713) opens from its chip.
const historyHint = (handOffId) => { const txId = displayTxId({ handOffId }); return txId ? ` Open its history (${txId}) to see where it is.` : ''; };
const RELEASE_NOT_ELIGIBLE_REASONS = {
  not_ended: () => 'This session has not ended yet, so it cannot be added to an invoice. It can be once it has ended and settled.',
  not_held: (handOffId) => `This cost is not waiting for approval, so there is nothing to approve here.${historyHint(handOffId)}`,
  // Deprecated by Spec 1.4.0 (ACCT-TX-005/010: never emitted; a repeat release is a 200). Kept only for an older Core, and it
  // claims nothing Core has not told us: Core's record of the release is not the Ledger having the cost.
  already_released: (handOffId) => {
    const txId = displayTxId({ handOffId });
    return `Core has already accepted the release of this cost. Whether the Ledger has received it is not shown here. If it does not appear on the next invoice, open its history${txId ? ` (${txId})` : ''} or check Problems in Billing.`;
  },
};
const RELEASE_NOT_FOUND_MESSAGE = (handOffId) => `This cost is no longer waiting for release.${historyHint(handOffId)} The view has been refreshed.`;
// M004-F0775 (T-M004-0379): an unknown outcome on release. No failure text: the Ledger may have applied the release.
const RELEASE_UNCONFIRMED_TEXT = 'We couldn\'t confirm whether this went through. We\'ve refreshed what the Ledger shows — check it before trying again.';
const RELEASE_IDEMPOTENCY_MESSAGE = 'This release was already sent with different details. Check the view and try again.';
export const releaseRefusalMessage = (error, handOffId = '') => {
  const code = errorCode(error);
  if (code === RELEASE_NOT_FOUND) return RELEASE_NOT_FOUND_MESSAGE(handOffId);
  if (code === RELEASE_IDEMPOTENCY_CONFLICT) return RELEASE_IDEMPOTENCY_MESSAGE;
  if (code === RELEASE_NOT_ELIGIBLE) {
    // 1.4.0 (voided, refused, billed_elsewhere): the refusal carries Core's own `message` and `next_action`; shown verbatim.
    const body = error?.response?.data || {};
    const reasonText = RELEASE_NOT_ELIGIBLE_REASONS[releaseRefusalReason(error)];
    if (reasonText) return reasonText(handOffId);
    const core = [body.message, body.next_action].filter((part) => typeof part === 'string' && part.trim()).join(' ');
    return core || 'This cost cannot be added to an invoice right now. The view has been refreshed.';
  }
  return '';
};
// Polling for a released item to land on the draft: the Ledger lands it through the notice path after Core posts.
const LANDING_POLL_MS = 4000;
const LANDING_POLL_MAX = 6;
// M004-F0716 (design 5.2 rows 2-3): what Core's 200 proves is that Core ACCEPTED the release, not that the Ledger has the cost.
const WAITING_MESSAGE = 'Release accepted by Core. Waiting for the Ledger to confirm…';
// Shown once the landing polls are spent and the released item is still listed (never a promise that it is arriving).
const stillWaitingMessage = (handOffId) => { const txId = displayTxId({ handOffId }); return `The Ledger has not confirmed this yet. Open its history${txId ? ` (${txId})` : ''} to see where it is, or check Problems in Billing.`; };
const PROBLEMS_HINT = 'These costs were approved but have not reached the Ledger, so they are not on an invoice yet. Fix them in Billing.';
export const billingProblemsHref = (orgId) => `/app/bookings?tab=billing&view=prob&org=${encodeURIComponent(orgId)}`;
export const QUEUES_DENIED_MESSAGE = "You can't manage invoices on this account: it's your own account, or you don't have Ledger manage rights for this organisation.";
// M004-F0723: the PDF preview helpers. base64 -> Blob; never throws on a malformed body (returns null).
const SEVERITY_LABEL = { blocker: 'Blocker', warning: 'Warning', info: 'Info' };
// The decoded size follows from the base64 text (4 chars -> 3 bytes, less the '=' padding), so an oversized body is
// refused before it is decoded. A length that is not a multiple of 4 is not trusted here; it falls through to atob.
const decodedByteLength = (base64) => {
  if (base64.length % 4 !== 0) return null;
  const padding = base64.endsWith('==') ? 2 : (base64.endsWith('=') ? 1 : 0);
  return (base64.length / 4) * 3 - padding;
};
const decodePdf = (base64) => {
  try {
    const expected = decodedByteLength(base64);
    if (expected !== null && expected > PREVIEW_MAX_BYTES) return 'too_large';
    const binary = globalThis.atob(base64);
    if (binary.length > PREVIEW_MAX_BYTES) return 'too_large';
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], { type: 'application/pdf' });
  } catch (cause) { return null; }
};
const downloadBlob = (url, filename) => {
  const link = document.createElement('a');
  link.href = url; link.download = filename; link.rel = 'noopener';
  document.body.appendChild(link); link.click(); link.remove();
};
// M004-F0730: a readiness blocker names a billing-settings field; the link opens the settings drawer on it.
const BILLING_FIELD_FOR_READINESS = Object.freeze({ issuer_name_missing: 'issuer_name', issuer_address_missing: 'issuer_address', bank_details_missing: 'bank_details', payment_terms_missing: 'payment_terms_text', invoice_prefix_missing: 'invoice_prefix' });
const newKey = (prefix) => globalThis.crypto?.randomUUID?.() || `${prefix}-${Date.now()}-${Math.random()}`;
const apiError = (error, fallback) => error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || fallback;

// WebUI #167 / #180: a draft line is LOCKED once its ledger entry is WRITTEN (Spec 1.12.0 TransactionStage 'posted': the read-only boundary).
const WRITTEN_STAGES = ['posted', 'paid', 'credited'];
// M004-F0741 (amended): the sentence the drawer gives for a locked line, by stage. '' means the line is editable.
// A 'posted' line still on a draft is the approve crash window (the Ledger wrote it, the invoice was not finished): approving again finishes it.
const LOCK_WRITTEN = 'Locked: this entry is written to the Ledger, so it can no longer be edited.';
const LOCK_POSTED_ON_DRAFT = 'Approve the invoice again to finish issuing it.';
const LOCK_ISSUED = 'Issued invoices cannot be changed.';
export const lineLockText = (stage) => {
  if (stage === 'issued') return LOCK_ISSUED;
  if (stage === 'posted') return LOCK_POSTED_ON_DRAFT;
  return WRITTEN_STAGES.includes(stage) ? LOCK_WRITTEN : '';
};
const isLineLocked = (stage) => WRITTEN_STAGES.includes(stage) || stage === 'issued';
// M004-F0741 (amended): why the viewer has no Edit. Wording follows the real gate: ledger_manage (canEditText) and not the own account (canAct),
// with the SAL 4 step-up asked at the write.
const NO_EDIT_NO_PERMISSION = 'Editing an invoice line needs Ledger manage permission and a fresh step-up sign-in.';
const NO_EDIT_OWN_ACCOUNT = 'You cannot change money on your own account. Another billing manager needs a fresh step-up sign-in to edit it.';

// §2.44.6 amount rules: finite, >= 0, at most 2 decimal places.
export function parsePounds(value) {
  const text = String(value ?? '').replace(/[£,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const amount = Number(text);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

// M004-F0727: the reason sent when no prompt was shown (the contract requires one). Same words as the Ledger's own move.
export function composeReleaseReason(item, actor) {
  const verb = item.sessionEnded ? 'ended' : 'started';
  return `Session ${verb} ${formatDay(item.date)} — added to invoice by ${actor || 'a manager'}`;
}
// Ledger #115 / ML001-F0198: an unsettled notice that is no longer `coming` is waiting for Core, never "Being checked"
// (that is a SETTLED cost whose landing was refused). Core's own stage says whether it waits for approval.
const pillLabel = (item) => (item.awaitingApproval ? (item.noticeState === 'awaiting_approval' ? 'Waiting for approval in Core' : 'Waiting for Core') : PILL[item.pill]);

// The page's one poll budget (WebUI #190): Ledger re-reads after a tunnel timeout or a pending answer, at the Ledger's own cadence
// (DEFAULT_RETRY_SECONDS). Shared by the approve watch and the drawer edit's confirm-by-re-read; never raised.
const ISSUE_WATCH_MAX = 12;
// Drawer edit, tunnel timeout: the words shown while the page checks the Ledger, and when the check never matched.
const EDIT_CHECKING_TEXT = 'Saving — checking the Ledger…';
const EDIT_NOT_CONFIRMED_TEXT = 'The Ledger has not confirmed this change yet, and it may still be applying it. Press Save to check again: the same request is re-sent, so it cannot be applied twice.';
const EDIT_UNCONFIRMED = Symbol('edit-unconfirmed');

// ML001-F0239: `canAct` is the money-action gate (canPerformMoneyActions: ledger_manage AND not the viewer's own account).
// Default false (fail closed): the queues then render read-only, with no write control and no write path.

// M004-F0799: the draft read and the preview take the selector only with more than one open draft; with one, the call is unchanged.
// M004-F0799 (lazy): a draft read that answers 409 SELECTOR_REQUIRED resolves to this marker, so the load can re-read draft.list and show the switcher.
const SELECTOR_REQUIRED_READ = Symbol('selector-required-read');
const readDraftWith = (orgId, userId, selector) => (selector.invoiceId ? ledgerAccountApi.getDraft(orgId, userId, selector) : ledgerAccountApi.getDraft(orgId, userId));
const previewWith = (orgId, userId, selector) => (selector.invoiceId ? ledgerAccountApi.previewDraft(orgId, userId, selector) : ledgerAccountApi.previewDraft(orgId, userId));

export default function LedgerQueueSections({ orgId, userId, customerName, organisationName, money, onIssued, actorName = '', canAct = false, canEditText = false }) {
  const [status, setStatus] = useState('loading');
  const [advice, setAdvice] = useState([]);
  // M004-F0736: Reject / Restore ask for the owner's reason in a dialog; the Rejected (archive) tag is collapsed until opened.
  const [reasonAsk, setReasonAsk] = useState(null);
  const [reasonError, setReasonError] = useState('');
  const [archiveOpen, setArchiveOpen] = useState(false);
  // M004-F0774: the Advice card is collapsed unless this viewer opened it (remembered per browser; a storage failure means collapsed).
  const [cardOpen, setCardOpen] = useState(readAdviceCardOpen);
  const [draft, setDraft] = useState(null);
  // M004-F0711: this customer's claimed postings in Core's billing `prob` view. Empty on any failure of that read.
  const [problems, setProblems] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [adviceError, setAdviceError] = useState('');
  const [approvalError, setApprovalError] = useState('');
  const [busy, setBusy] = useState(false);
  // WebUI #167: a row opens its item's drawer (Details: the one Edit form, then the history). `openItem` is { kind, id }.
  const [openItem, setOpenItem] = useState(null);
  const [detailsMessage, setDetailsMessage] = useState(null);
  // The edits saved in this view, one line per changed field, keyed `${kind}:${id}` (the drawer's Edit history).
  const [sessionEdits, setSessionEdits] = useState({});
  const [adding, setAdding] = useState(false);
  const [manualForm, setManualForm] = useState({ description: '', amount: '' });
  const [confirming, setConfirming] = useState(false);
  const [issued, setIssued] = useState(null);
  // WebUI #190 (Core #398): an approve Core answers PENDING (202, or the first request's 504 TUNNEL_NODE_TIMEOUT) is still being issued.
  // `issuing` is set while the page re-reads the draft; `issueNotice` says what the re-read found (never an error).
  const [issuing, setIssuing] = useState(false);
  const [issueNotice, setIssueNotice] = useState('');
  // One Idempotency-Key per draft: a repeat of the same approve reuses it, so the Ledger replays and never issues twice.
  const issueKey = useRef(null);
  // Synchronous guard: two clicks in one tick are two events before React re-renders the disabled state.
  const approveInFlight = useRef(false);
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  // ML001-F0122, ML001-F0123: the release confirm step (one item at a time) and the items released but not yet landed.
  const [releasing, setReleasing] = useState(null);
  const [releaseReason, setReleaseReason] = useState('');
  const releaseInFlight = useRef(false);
  const releaseAttempt = useRef(null);
  const [landing, setLanding] = useState([]);
  const [adviceNotice, setAdviceNotice] = useState('');
  const [polls, setPolls] = useState(0);
  const pendingWrite = useRef(null);
  // Drawer edits the Ledger did not confirm after a tunnel timeout: field+target+value+reason -> the Idempotency-Key already sent.
  // A re-send of the SAME edit reuses it (the Ledger replays, never applies twice); a different value or reason is a new edit.
  const editAttempts = useRef(new Map());
  // M004-F0723: Preview (PDF). Its own state: it never touches busy, the draft, the queues or storage.
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewMessage, setPreviewMessage] = useState('');
  const [previewError, setPreviewError] = useState('');
  const previewInFlight = useRef(false);
  // M004-F0799: the open drafts (draft.list) and the chosen one. The chosen invoice_id goes on every draft read and write when there are several.
  const [drafts, setDrafts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectorSentence, setSelectorSentence] = useState('');
  const selectedRef = useRef(null);
  const draftsRef = useRef([]);
  const selectorNow = () => selectorFor(draftsRef.current, selectedRef.current);
  const readDraftList = useCallback(async () => draftsOf((await customerBillingProfileApi.draftList(orgId, userId))?.data?.data), [orgId, userId]);
  // A 409 LEDGER_DRAFT_SELECTOR_REQUIRED: re-read draft.list, show the switcher with the sentence, and wait for the choice.
  const reselect = useCallback(async () => {
    const list = await readDraftList();
    draftsRef.current = list;
    setDrafts(list); setDraft(null); selectedRef.current = null; setSelectedId(null); setSelectorSentence(SELECTOR_SENTENCE);
    setStatus('ready');
  }, [readDraftList]);

  const load = useCallback(async () => {
    // A client without the read operations cannot offer the sections at all:
    // the same outcome as Core answering them unsupported.
    if (typeof ledgerAccountApi?.listAdvice !== 'function' || typeof ledgerAccountApi?.getDraft !== 'function') {
      setStatus('unsupported');
      return;
    }
    try {
      // M004-F0799 (lazy): the draft is read WITHOUT a selector, exactly as on main. draft.list is read only after a 409 SELECTOR_REQUIRED
      // (reselect). Once a choice is known, selectorNow() carries its invoice_id; before that it is {}.
      const draftRead = readDraftWith(orgId, userId, selectorFor(draftsRef.current, selectedRef.current)).catch((cause) => {
        if (isSelectorRequired(cause)) return SELECTOR_REQUIRED_READ;
        throw cause;
      });
      const [adviceResponse, draftResponse] = await Promise.all([ledgerAccountApi.listAdvice(orgId, userId), draftRead]);
      setAdvice(normaliseAdvice(adviceResponse));
      if (draftResponse === SELECTOR_REQUIRED_READ) await reselect();
      else setDraft(normaliseDraft(draftResponse, money));
      setLoadError('');
      setStatus('ready');
      // M004-F0711: only after the queue reads succeed, so an unsupported or refused page stays the baseline. ANY failure of this
      // read (403 no billing rights, 404, 5xx, malformed) shows no Problems group; the sections above never depend on it.
      let found = [];
      if (typeof ledgerAccountApi?.listProblems === 'function') {
        try { found = normaliseProblems(await ledgerAccountApi.listProblems(orgId), userId); } catch { found = []; }
      }
      setProblems(found);
    } catch (cause) {
      // Prod Core (before Core #268 / fe4651c8 ships the gateway routes)
      // answers an unrecognised route with its own generic 404 NOT_FOUND —
      // never a contract-declared code. On the reads this closes the
      // capability gate exactly like LEDGER_ACCOUNT_OPERATION_UNSUPPORTED:
      // byte-identical to the baseline, no error banner (2026-10-01).
      if (isUnsupportedError(cause) || isRouteNotFoundError(cause)) setStatus('unsupported');
      // M004-F0708 (WebUI #142): 403 LEDGER_ACCOUNT_PERMISSION_DENIED is said out loud, never silent.
      else if (isPermissionDeniedError(cause)) setStatus('denied');
      else if (isForbiddenError(cause)) setStatus('forbidden');
      else { setLoadError(apiError(cause, 'The advice and the next invoice could not be loaded.')); setStatus('error'); }
    }
  }, [orgId, userId, money, reselect]);

  useEffect(() => { load(); }, [load]);

  // The switcher's choice: the chosen draft is read; nothing is sent until the page has it.
  const choose = (invoiceId) => {
    selectedRef.current = invoiceId;
    setSelectedId(invoiceId);
    setSelectorSentence('');
    setIssued(null);
    return load();
  };

  // M004-F0715/F0720 (W5/W9): moves, Advice amount edit and Bill by hand live in ./ledgerMoves (Ledger-only writes, never gated by Core).
  const moves = useLedgerMoves({ orgId, userId, money, draft, advice, reload: load, enabled: canAct === true, applyState: ({ advice: nextAdvice, draft: nextDraft }) => { setAdvice(nextAdvice); setDraft(nextDraft); setIssued(null); } });

  // Every write follows the page's step-up pattern (grantCredit): a step-up
  // challenge opens StepUpDialog, and completing it re-runs the same write.
  // `textOnly` (WebUI #167): a title-only edit on the viewer's own account. Allowed with ledger_manage (canEditText); never an amount.
  const runWrite = useCallback(async (write, setSectionError, refusalHandOffId = '', textOnly = false) => {
    if (canAct !== true && !(textOnly && canEditText === true)) return false; // ML001-F0239: no write leaves while the gate is closed
    setAdviceError(''); setApprovalError(''); setAdviceNotice('');
    setBusy(true);
    try {
      await write();
      pendingWrite.current = null;
      return true;
    } catch (cause) {
      // Step-up is Core's 401 STEP_UP_REQUIRED (contract ErrorUnauthenticated, the
      // same answer credit.grant gives). A 403, including the node's own
      // LEDGER_ACCOUNT_SAL_REQUIRED, is a refusal. It never opens the dialog.
      const challenge = Number(cause?.response?.status) === 403 ? null : getStepUpChallengeFromError(cause);
      if (challenge?.id) {
        pendingWrite.current = { write, setSectionError, refusalHandOffId, textOnly };
        setStepUpChallenge(challenge);
      } else if (isFeedNotBoundError(cause)) {
        // M004-F0710: Core wrote nothing, so there is nothing to reload. Core's own words, with the TX id.
        pendingWrite.current = null;
        setSectionError(feedNotBoundMessage(cause));
      } else if (KNOWN_REFUSALS[errorCode(cause)] || releaseRefusalMessage(cause, refusalHandOffId)) {
        pendingWrite.current = null;
        await load();
        setSectionError(KNOWN_REFUSALS[errorCode(cause)] || releaseRefusalMessage(cause, refusalHandOffId));
      } else {
        setSectionError(apiError(cause, 'The change could not be saved.'));
      }
      return false;
    } finally {
      setBusy(false);
    }
  }, [load, canAct, canEditText]);

  // One Idempotency-Key per user action. A step-up retry re-runs the same
  // closure, so it re-sends the same key.
  const edit = (params, setSectionError, after) => {
    const key = newKey(params.action);
    return runWrite(async () => {
      await ledgerAccountApi.editDraft(orgId, userId, { ...params, ...selectorNow() }, key);
      if (after) after();
      await load();
    }, setSectionError);
  };

  const adviceAction = (adviceId, action) => edit({ action, adviceId }, setAdviceError, () => setIssued(null));
  // M004-F0736: reject / restore never send until the owner has typed a reason (ReasonDialog).
  const askReason = (item, action) => { setReasonError(''); setAdviceError(''); setReasonAsk({ adviceId: item.adviceId, action, subject: [formatDay(item.date), item.description].filter(Boolean).join(' · ') }); };
  const saveReason = (reason) => {
    const { adviceId, action } = reasonAsk;
    return edit({ action, adviceId, reason }, setReasonError, () => { setReasonAsk(null); setReasonError(''); setIssued(null); });
  };
  // ML001-F0122, ML001-F0123: advice.release. SAL 4 through the same runWrite as every other write
  // (a step-up replays this closure, so the same Idempotency-Key), then reload; an item
  // the reload still lists is shown as "being added" and re-checked a few times.
  const release = (event, direct = null) => {
    if (event) event.preventDefault();
    // M004-F0727: with no prompt (the start is not ahead) the caller supplies the composed reason.
    const reason = (direct ? direct.reason : releaseReason).trim();
    if (!reason) { setAdviceError('A reason is required.'); return; }
    if (reason.length > 500) { setAdviceError('The reason must be 500 characters or fewer.'); return; }
    const handOffId = direct ? direct.handOffId : releasing;
    // Core #284: two concurrent same-key advice.release requests can get one 409
    // instead of an idempotent 200 on MariaDB. So one request per click: a
    // synchronous in-flight guard (the `busy` state disables the button only after
    // a re-render, which a double submit can beat), and a retry of the SAME
    // release (same item, same reason) re-sends the SAME Idempotency-Key. A
    // changed reason is a different release and gets a new key.
    if (releaseInFlight.current) return undefined;
    const previous = releaseAttempt.current;
    const key = previous && previous.handOffId === handOffId && previous.reason === reason ? previous.key : newKey('release');
    releaseAttempt.current = { handOffId, reason, key };
    releaseInFlight.current = true;
    return runWrite(async () => {
      let accepted;
      try {
        accepted = normaliseRelease(await ledgerAccountApi.releaseAdvice(orgId, userId, { handOffId, reason }, key));
      } catch (cause) {
        if (classifyRelayOutcome(cause) !== 'unknown') throw cause;
        // Unknown: keep releaseAttempt (the same key goes out on the next press of the same release), keep the form, re-read.
        setAdviceNotice(RELEASE_UNCONFIRMED_TEXT);
        await load();
        return;
      }
      releaseAttempt.current = null;
      setReleasing(null); setReleaseReason('');
      // M004-F0716 phase B (design 5.2 row 1): the sentence follows the STAGE of Core's 200; only sending_to_ledger (or no stage) waits.
      const outcome = releaseOutcome(accepted, handOffId);
      if (!outcome.waiting) {
        setAdviceNotice(outcome.notice);
      } else {
        setLanding((current) => (current.includes(handOffId) ? current : [...current, handOffId]));
        setPolls(0);
      }
      await load();
    }, setAdviceError, handOffId).finally(() => { releaseInFlight.current = false; });
  };

  const lineRemove = (lineId) => edit({ action: 'remove', lineId }, setApprovalError);

  // WebUI #167: the item drawer's one Edit form. Title first (draft.line.edit action 'description'), then amount (action 'amount' on
  // a draft line, the advice amount op on an Advice item), all with the one reason. Each request has its own Idempotency-Key, made
  // once per save so a step-up replay re-sends the SAME keys. A failure keeps the form open and says exactly what saved.
  const isStepUp = (cause) => Number(cause?.response?.status) !== 403 && Boolean(getStepUpChallengeFromError(cause)?.id);
  const refusalWords = (cause) => KNOWN_REFUSALS[errorCode(cause)] || apiError(cause, 'The change could not be saved.');
  const recordEdit = (entryKey, entry) => setSessionEdits((current) => {
    const list = current[entryKey] || [];
    if (list.some((row) => row.id === entry.id)) return current;
    return { ...current, [entryKey]: [...list, entry] };
  });
  const stamp = () => new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  const saveDetails = (target, { title, amount, reason, currentTitle, currentAmount }) => {
    const attempts = editAttempts.current;
    const attemptId = (field, value) => `${field}|${target.kind}:${target.id}|${value}|${reason}`;
    const titleId = attemptId('title', title);
    const amountId = attemptId('amount', amount);
    const titleKey = title !== undefined ? (attempts.get(titleId) || newKey('edit-title')) : null;
    const amountKey = amount !== undefined ? (attempts.get(amountId) || newKey('edit-amount')) : null;
    const entryKey = `${target.kind}:${target.id}`;
    const by = actorName || 'a manager';
    const textOnly = canAct !== true;
    // A tunnel timeout is not a refusal: the Ledger may well have applied the edit (Core cuts the relay at 3 s). Wait, re-read with
    // the page's own poll budget, and call it saved only when the stored value is what was sent.
    const confirmSaved = async (read) => {
      setDetailsMessage({ tone: 'pending', text: EDIT_CHECKING_TEXT });
      for (let reads = 0; reads < ISSUE_WATCH_MAX; reads += 1) {
        await new Promise((resolve) => setTimeout(resolve, DEFAULT_RETRY_SECONDS * 1000)); // eslint-disable-line no-await-in-loop
        try { if (await read()) return true; } catch { /* any failed re-read is retried on the next one */ } // eslint-disable-line no-await-in-loop
      }
      return false;
    };
    const draftLineIs = (matches) => async () => {
      const current = normaliseDraft(await readDraftWith(orgId, userId, selectorNow()), money);
      const line = current && current.lines.find((row) => row.lineId === target.id);
      return Boolean(line) && matches(line);
    };
    const adviceIs = (matches) => async () => {
      const item = normaliseAdvice(await ledgerAccountApi.listAdvice(orgId, userId)).find((row) => row.adviceId === target.id);
      return Boolean(item) && matches(item);
    };
    const sameAmount = (stored) => Number.isFinite(stored) && Math.round(stored * 100) === Math.round(amount * 100);
    return runWrite(async () => {
      const failure = async (call, id, key, confirm, words = refusalWords) => {
        try { await call(); attempts.delete(id); return ''; } catch (cause) {
          if (isStepUp(cause)) throw cause;
          if (isTunnelTimeout(cause)) {
            if (await confirmSaved(confirm)) { attempts.delete(id); return ''; }
            attempts.set(id, key);
            return EDIT_UNCONFIRMED;
          }
          attempts.delete(id);
          return words(cause);
        }
      };
      const saved = [];
      let message = null;
      if (target.kind === 'draft') {
        if (title !== undefined) {
          const problem = await failure(() => ledgerAccountApi.editDraft(orgId, userId, { action: 'description', lineId: target.id, description: title, reason, ...selectorNow() }, titleKey), titleId, titleKey, draftLineIs((line) => line.description === title));
          if (problem === EDIT_UNCONFIRMED) message = { tone: 'error', text: `Title not confirmed. ${EDIT_NOT_CONFIRMED_TEXT}` };
          else if (problem) message = { tone: 'error', text: `Title not changed: ${problem}${amount !== undefined ? ' Amount not changed.' : ''}` };
          else {
            saved.push('title');
            recordEdit(entryKey, { id: titleKey, at: stamp(), field: 'Title', from: `“${currentTitle}”`, to: `“${title}”`, reason, by });
          }
        }
        if (!message && amount !== undefined) {
          const problem = await failure(() => ledgerAccountApi.editDraft(orgId, userId, { action: 'amount', lineId: target.id, amount, reason, ...selectorNow() }, amountKey), amountId, amountKey, draftLineIs((line) => sameAmount(line.amount)));
          if (problem === EDIT_UNCONFIRMED) message = { tone: 'error', text: `${saved.length ? 'Title saved; amount' : 'Amount'} not confirmed. ${EDIT_NOT_CONFIRMED_TEXT}` };
          else if (problem) message = { tone: 'error', text: saved.length ? `Title saved; amount not changed: ${problem}` : `Amount not changed: ${problem}` };
          else {
            saved.push('amount');
            recordEdit(entryKey, { id: amountKey, at: stamp(), field: 'Amount', from: money(currentAmount, draft?.currency), to: money(amount, draft?.currency), reason, by });
          }
        }
      } else if (amount !== undefined) {
        // The advice amount op keeps its own sentences (the written-TX credit-note path, ACCT-TX-019).
        const problem = await failure(() => ledgerAccountApi.editAdviceAmount(orgId, userId, { adviceId: target.id, amount, reason }, amountKey), amountId, amountKey, adviceIs((item) => sameAmount(item.amount)), (cause) => amountRefusal(cause, target.handOffId).text);
        if (problem === EDIT_UNCONFIRMED) message = { tone: 'error', text: `Amount not confirmed. ${EDIT_NOT_CONFIRMED_TEXT}` };
        else if (problem) message = { tone: 'error', text: `Amount not changed: ${problem}` };
        else {
          saved.push('amount');
          recordEdit(entryKey, { id: amountKey, at: stamp(), field: 'Amount', from: money(currentAmount, draft?.currency), to: money(amount, draft?.currency), reason, by });
        }
      }
      if (!message) message = { tone: 'ok', text: `Saved: ${saved.join(' and ')}.` };
      setDetailsMessage({ ...message });
      await load();
    }, (text) => setDetailsMessage({ tone: 'error', text }), '', textOnly);
  };

  const saveManual = (event) => {
    event.preventDefault();
    const amount = parsePounds(manualForm.amount);
    const description = manualForm.description.trim();
    if (!description) { setApprovalError('Enter a description for the line.'); return; }
    if (amount == null) { setApprovalError('Enter an amount in pounds, for example 45.00.'); return; }
    edit({ action: 'add_manual', description, amount }, setApprovalError, () => {
      setAdding(false);
      setManualForm({ description: '', amount: '' });
      setIssued(null);
    });
  };

  // WebUI #190 / Core #398. A re-read cadence of the Ledger's own retry_after_seconds, bounded: after ISSUE_WATCH_MAX re-reads the page
  // says it is still issuing and stops (the draft is never refused as an error for a successful issue).
  const watchIssue = async (invoiceId, seconds) => {
    setIssuing(true);
    setIssueNotice('');
    let gone = false;
    try {
      for (let read = 0; read < ISSUE_WATCH_MAX && !gone; read += 1) {
        await new Promise((resolve) => setTimeout(resolve, seconds * 1000));
        try {
          const current = normaliseDraft(await readDraftWith(orgId, userId, selectorNow()), money);
          gone = !current || current.invoiceId !== invoiceId;
        } catch (cause) {
          // 404 on the draft read: there is no open draft any more. Any other failure is retried on the next read.
          gone = isDraftNotFoundError(cause);
        }
      }
    } finally {
      setIssuing(false);
    }
    // The confirm step reads the draft's total: close it BEFORE the reload empties the draft (as the issued path does).
    if (gone) { issueKey.current = null; setConfirming(false); }
    await load();
    if (gone) {
      setIssueNotice('The draft has left the queue. Check the issued invoices below.');
      if (onIssued) await onIssued();
    } else {
      setIssueNotice('The Ledger is still issuing this invoice. Nothing was sent twice. Check the issued invoices below, or try again shortly.');
    }
  };

  const approve = () => {
    // A click while an approve is in flight (or the issue is pending) sends nothing.
    if (issuing || approveInFlight.current) return undefined;
    approveInFlight.current = true;
    const { invoiceId, total } = draft;
    if (!issueKey.current || issueKey.current.invoiceId !== invoiceId) issueKey.current = { invoiceId, key: newKey('approve') };
    const key = issueKey.current.key;
    return runWrite(async () => {
      // §2.44.12.5: echo the total the owner saw. A changed draft is refused, never issued unseen.
      let answer;
      try {
        answer = await ledgerAccountApi.approveDraft(orgId, userId, { invoiceId, expectedTotal: total }, key);
      } catch (cause) {
        // The first request can pass Core's 3 s tunnel SLA while the Ledger issues. That is pending, not a failed issue.
        if (!isTunnelTimeout(cause)) throw cause;
        answer = { status: 202, data: { success: true, data: { status: 'pending', code: PENDING_CODE, retry_after_seconds: DEFAULT_RETRY_SECONDS } } };
      }
      const pending = pendingAnswer(answer);
      if (pending) {
        await watchIssue(invoiceId, pending.retryAfterSeconds);
        return;
      }
      issueKey.current = null;
      const result = normaliseIssued(answer);
      setConfirming(false);
      setIssued(result);
      await load();
      if (onIssued) await onIssued();
    }, setApprovalError).finally(() => { approveInFlight.current = false; });
  };

  // M004-F0723: GET …/draft/preview. Read-only; never calls approve, never reloads on success.
  const openPreview = async () => {
    if (previewInFlight.current) return;
    previewInFlight.current = true;
    setPreviewing(true); setPreviewError(''); setPreviewMessage('');
    try {
      const result = normalisePreview(await previewWith(orgId, userId, selectorNow()));
      if (!result) { setPreviewError('The preview could not be prepared. Try again.'); return; }
      const blob = decodePdf(result.pdfBase64);
      if (blob === 'too_large') { setPreview(result); setPreviewError('The preview is too large to open here.'); return; }
      if (!blob) { setPreviewError('The preview could not be read. Try again.'); return; }
      setPreview(result);
      const url = URL.createObjectURL(blob);
      const tab = window.open(url, '_blank');
      if (tab) { try { tab.opener = null; } catch (cause) { /* cross-origin guard */ } setPreviewMessage('Preview opened in a new tab. Nothing has been issued.'); }
      else { downloadBlob(url, result.filename); setPreviewMessage(`Your browser blocked the new tab, so the preview was downloaded as ${result.filename}. Nothing has been issued.`); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (cause) {
      if (isSelectorRequired(cause)) { setPreviewError(SELECTOR_SENTENCE); await load(); }
      else if (isDraftNotFoundError(cause)) { setPreview(null); setPreviewError('There is no open draft to preview. The view has been refreshed.'); await load(); }
      else if (isForbiddenError(cause)) setPreviewError(QUEUES_DENIED_MESSAGE);
      else setPreviewError(apiError(cause, 'The preview could not be prepared. Try again.'));
    } finally { previewInFlight.current = false; setPreviewing(false); }
  };

  // A released item that has left Advice has landed; one still listed is "being added" and re-checked a few times.
  useEffect(() => {
    if (!landing.length) return undefined;
    const stillListed = landing.filter((id) => advice.some((entry) => entry.handOffId === id));
    if (stillListed.length < landing.length) {
      const left = landing.filter((id) => !stillListed.includes(id));
      setLanding(stillListed);
      // M004-F0716 (design 5.2 row 4): leaving the Advice list is NOT arriving on the draft. "Added" is said only when the reloaded
      // draft holds a line with the same hand_off_id; otherwise the sentence says only what is known.
      const onDraft = (id) => (draft?.lines || []).some((line) => sameHandOff(line.handOffId, id));
      const missing = left.filter((id) => !onDraft(id));
      if (!missing.length) setAdviceNotice('Added. It is on the next invoice below.');
      else {
        const txId = displayTxId({ handOffId: missing[0] });
        setAdviceNotice(`It has left Advice. Check the next invoice below, or open its history${txId ? ` (${txId})` : ''}.`);
      }
      return undefined;
    }
    if (polls >= LANDING_POLL_MAX) return undefined;
    const timer = setTimeout(() => { setPolls((count) => count + 1); load(); }, LANDING_POLL_MS);
    return () => clearTimeout(timer);
  }, [landing, advice, draft, polls, load]);

  if (status === 'loading' || status === 'unsupported' || status === 'forbidden') return null;
  if (status === 'denied') return <section className="ledger-account-section ledger-queue-section" data-testid="ledger-queues-denied"><p className="ledger-queue-muted">{QUEUES_DENIED_MESSAGE}</p></section>;

  // M004-F0711: an Advice item Core reports as a problem moves INTO its Problems row (never listed twice).
  const problemFor = (item) => problems.find((row) => sameHandOff(row.handOffId, item.handOffId));
  const listedAdvice = advice.filter((item) => item.pill !== 'rejected' && !problemFor(item));
  // M004-F0736: rejected items live only under the Rejected tag, never in the working list.
  const archived = advice.filter((item) => item.pill === 'rejected');
  const adviceFor = (row) => advice.find((item) => sameHandOff(item.handOffId, row.handOffId));
  const lines = draft?.lines || [];
  const hasLines = lines.length > 0;
  const closeItem = () => { setOpenItem(null); setDetailsMessage(null); };
  // WebUI #167: the item drawer. Read from the current lists, so a save's reload shows the new values; a missing item closes nothing.
  const openLine = openItem?.kind === 'draft' ? (lines.find((line) => line.lineId === openItem.id) || openItem.snapshot) : null;
  const openAdvice = openItem?.kind === 'advice' ? (advice.find((item) => item.adviceId === openItem.id) || openItem.snapshot) : null;
  const drawerTarget = openLine ? { kind: 'draft', id: openLine.lineId, handOffId: openLine.handOffId, item: { description: openLine.description, amount: openLine.amount, currency: draft?.currency }, stage: openLine.stage, audit: openLine.audit, locked: isLineLocked(openLine.stage), lockText: lineLockText(openLine.stage), startInEdit: openItem.edit === true }
    : openAdvice ? { kind: 'advice', id: openAdvice.adviceId, handOffId: openAdvice.handOffId, item: { description: openAdvice.description, amount: openAdvice.amount, currency: openAdvice.currency }, stage: '', audit: [], locked: false, date: openAdvice.date, pill: openAdvice.pill }
      : null;
  const drawerKey = drawerTarget ? `${drawerTarget.kind}:${drawerTarget.id}` : '';
  const openDrawer = drawerTarget ? <TxHistoryDrawer key={drawerKey}
    request={{ key: drawerTarget.handOffId || drawerTarget.id, orgId, customerUserId: userId, handOffId: drawerTarget.handOffId }}
    noHistory={!drawerTarget.handOffId} headingText={drawerTarget.handOffId ? '' : (drawerTarget.kind === 'draft' ? 'Invoice line' : 'Advice item')}
    onClose={closeItem} onOpenOther={() => {}}
    details={<ItemDetails key={drawerKey} kind={drawerTarget.kind} item={drawerTarget.item}
      stageText={drawerTarget.kind === 'draft' ? stageLabel(drawerTarget.stage) : pillLabel(openAdvice)}
      dateText={drawerTarget.kind === 'advice' ? formatDay(drawerTarget.date) : ''}
      txText={displayTxId({ handOffId: drawerTarget.handOffId })}
      canEdit={canAct === true} canEditText={canEditText === true} locked={drawerTarget.locked}
      lockText={drawerTarget.lockText || LOCK_WRITTEN} startInEdit={drawerTarget.startInEdit === true}
      noEditText={canEditText === true ? NO_EDIT_OWN_ACCOUNT : NO_EDIT_NO_PERMISSION}
      money={money} draftTotal={draft?.total ?? null} audit={drawerTarget.audit}
      sessionEdits={sessionEdits[drawerKey] || []} message={detailsMessage} busy={busy}
      onSave={(payload) => saveDetails(drawerTarget, { ...payload, currentTitle: drawerTarget.item.description, currentAmount: drawerTarget.item.amount })} />}
  /> : null;

  // M004-F0774: forced open while a Problem row exists or the queue read failed (an error must not hide in a closed card).
  const forcedOpen = problems.length > 0 || status === 'error';
  const cardIsOpen = forcedOpen || cardOpen;
  const toggleCard = () => {
    if (forcedOpen) return;
    const next = !cardOpen;
    setCardOpen(next);
    writeAdviceCardOpen(next);
  };

  const closeForms = () => { setAdding(false); setConfirming(false); setApprovalError(''); };
  // WebUI #167: a row opens its drawer (a click on a control inside the row does not).
  // The snapshot is the row as it was clicked: a save's reload may move the item (an Advice item becomes a draft line), and the drawer
  // must stay on it so the message says what happened.
  const openRow = (event, kind, id, snapshot, edit = false) => {
    if (!edit && event.target.closest && event.target.closest('button,a,input,textarea,select,label,form')) return;
    setDetailsMessage(null);
    setOpenItem({ kind, id, snapshot, edit });
  };
  const rowKeyDown = (event, kind, id, snapshot) => { if (event.key === 'Enter' && event.target === event.currentTarget) openRow(event, kind, id, snapshot); };

  return <>
    <StepUpDialog challenge={stepUpChallenge} title="Verify invoice change" description="Confirm your identity before changing this customer's invoice."
      onComplete={() => { const pending = pendingWrite.current; setStepUpChallenge(null); if (pending) runWrite(pending.write, pending.setSectionError, pending.refusalHandOffId, pending.textOnly); }}
      onCancel={() => { const pending = pendingWrite.current; pendingWrite.current = null; setStepUpChallenge(null); if (pending) pending.setSectionError('Verification was cancelled.'); }} />

    {moves.dialog}

    {openDrawer}

    {reasonAsk ? <ReasonDialog action={reasonAsk.action} subject={reasonAsk.subject} busy={busy} error={reasonError}
      onSave={saveReason} onCancel={() => { setReasonAsk(null); setReasonError(''); }} /> : null}

    {/* M004-F0807 (WebUI #270 M4a): calendar update section. Gated off (CALENDAR_UPDATE_VIEW_ENABLED=false): renders nothing and sends no proposal.* frame. */}
    <CalendarUpdateSection orgId={orgId} canManage={canEditText} />

    <section className="ledger-account-section ledger-queue-section" data-testid="ledger-advice-section" aria-labelledby="ledger-advice-heading">
      {/* M004-F0774 (WebUI #246 phase 1): the section is a collapsible card. The header (title, hint, summary, chevron) is always visible; the one native button
          covers the whole header row. FORCED OPEN while a Problem row exists or the read failed: aria-expanded stays true, the button is aria-disabled (a click does nothing)
          and a visible line says why, so a problem is never hidden. */}
      <div className="ledger-advice-head">
        <h3 id="ledger-advice-heading">Advice</h3>
        <p className="ledger-queue-hint">Costs not on an invoice yet. A settled cost is put on the next invoice by the Ledger; one still waiting for Core can be added now.</p>
        {status === 'ready' ? <p className="ledger-advice-summary" data-testid="ledger-advice-summary">{adviceMonthSummary(listedAdvice, money, new Date(Date.now()))}</p> : null}
        {forcedOpen ? <p className="ledger-queue-muted ledger-advice-forced" data-testid="ledger-advice-forced">Kept open while something needs attention.</p> : null}
        <button type="button" className="ledger-advice-toggle" data-testid="ledger-advice-toggle" aria-labelledby="ledger-advice-heading"
          aria-expanded={cardIsOpen} aria-controls="ledger-advice-body" aria-disabled={forcedOpen ? 'true' : undefined} onClick={toggleCard}>
          <span className="ledger-advice-chevron" aria-hidden="true" />
        </button>
      </div>
      {cardIsOpen ? <div id="ledger-advice-body" className="ledger-advice-body" data-testid="ledger-advice-body">
      {status === 'error' ? <p className="ledger-account-error" role="alert">{loadError}</p> : null}
      {adviceError ? <p className="ledger-account-error" role="alert">{adviceError}</p> : null}
      {adviceNotice ? <p className="ledger-queue-muted" role="status" data-testid="ledger-advice-notice">{adviceNotice}</p> : null}
      <MoveStatus moves={moves} scope="advice" />
      {status === 'ready' && problems.length ? <div className="ledger-queue-problems" data-testid="ledger-advice-problems">
        <h4>Problems</h4>
        <p className="ledger-queue-hint">{PROBLEMS_HINT}</p>
        <ul className="ledger-advice-list">
          {problems.map((row) => {
            const match = adviceFor(row);
            return <li className="ledger-advice-line" key={row.itemId} data-testid="ledger-problem-line">
              <span className="ledger-queue-pill pill-problem">Problem</span>
              <span className="ledger-advice-desc">{[formatDay(match?.date || row.date), match?.description || row.description].filter(Boolean).join(' · ')}<TxChip handOffId={row.handOffId} orgId={orgId} customerUserId={userId} />
                {/* M004-F0716 phase B: a Ledger-held item (match with a stage) is described by the LEDGER's stage; Core's dispatch text is its stale mirror. */}
                {match?.stage ? <StageLine stage={match.stage} detail={match.pill} handOffId={row.handOffId} />
                  : (row.problemText ? <span className="ledger-queue-muted"> · {row.problemText}</span> : null)}</span>
              <span className="ledger-queue-money">{match ? money(match.amount, match.currency) : money(row.amount, row.currency)}</span>
              <span className="ledger-queue-actions">
                {canAct && match?.canReject ? <button type="button" className="ledger-queue-link danger" disabled={busy} data-testid="ledger-advice-reject" onClick={() => askReason(match, 'reject')}>Reject</button> : null}
                {/* W5/W9: Core's problem state never gates a move. A Ledger-held item (match) keeps Move (its amount is edited in the item drawer); a Core-only row can be billed by hand. */}
                {match ? <AdviceMoveActions item={match} moves={moves} disabled={busy} canBillByHand={false} /> : <ProblemBillByHand row={{ handOffId: row.handOffId }} moves={moves} disabled={busy} />}
                {canAct && match?.canRestore ? <button type="button" className="ledger-queue-link" disabled={busy} onClick={() => askReason(match, 'restore')}>Restore</button> : null}
                <Link className="ledger-queue-link" to={billingProblemsHref(orgId)}>Open in Billing</Link>
              </span>
              {match ? <AdviceMoveForms item={match} moves={moves} customerName={customerName} /> : null}
              {!match ? <ProblemBillByHandForm row={{ handOffId: row.handOffId, description: row.description, amount: row.amount }} moves={moves} customerName={customerName} /> : null}
            </li>;
          })}
        </ul>
      </div> : null}
      {status === 'ready' ? (listedAdvice.length || problems.length ? (listedAdvice.length ? <ul className="ledger-advice-list">
        {listedAdvice.map((item) => <li className="ledger-advice-line ledger-row-open" key={item.adviceId} data-testid="ledger-advice-line" tabIndex={0}
          onClick={(event) => openRow(event, 'advice', item.adviceId, item)} onKeyDown={(event) => rowKeyDown(event, 'advice', item.adviceId, item)}>
          <span className={`ledger-queue-pill pill-${item.pill}`}>{pillLabel(item)}</span>
          <span className="ledger-advice-desc">{[formatDay(item.date), item.description].filter(Boolean).join(' · ')}<TxChip handOffId={item.handOffId} orgId={orgId} customerUserId={userId} />{(item.note || DEFAULT_NOTE[item.pill]) ? <span className="ledger-queue-muted"> · {item.note || DEFAULT_NOTE[item.pill]}</span> : null}</span>
          <span className="ledger-queue-money">{money(item.amount, item.currency)}</span>
          <span className="ledger-queue-actions">
            {canAct && item.canRelease && !landing.includes(item.handOffId) ? <button type="button" className="ledger-queue-link" data-testid="ledger-advice-release" disabled={busy} onClick={() => {
              setAdviceError('');
              if (item.promptReason) { setReleasing(item.handOffId); setReleaseReason(''); return; }
              release(null, { handOffId: item.handOffId, reason: composeReleaseReason(item, actorName) });
            }}>Approve and send</button> : null}
            {landing.includes(item.handOffId) ? <span className="ledger-queue-muted" role="status" data-testid="ledger-advice-landing">{polls >= LANDING_POLL_MAX ? stillWaitingMessage(item.handOffId) : WAITING_MESSAGE}</span> : null}
            {canAct && item.canAdd && !moves.canMove(item) ? <button type="button" className="ledger-queue-link" disabled={busy} onClick={() => adviceAction(item.adviceId, 'add_from_advice')}>Add to invoice</button> : null}
            <AdviceMoveActions item={item} moves={moves} disabled={busy} />
            {canAct && item.canReject ? <button type="button" className="ledger-queue-link danger" disabled={busy} data-testid="ledger-advice-reject" onClick={() => askReason(item, 'reject')}>Reject</button> : null}
          </span>
          {canAct && releasing && releasing === item.handOffId && item.canRelease && item.promptReason ? <form className="ledger-queue-form" data-testid="ledger-advice-release-form" onSubmit={release} noValidate>
            <span>Add this session to the next invoice now, instead of waiting for it to start? Core posts it at once.</span>
            <label>Reason (required)<input required maxLength={500} value={releaseReason} onChange={(event) => setReleaseReason(event.target.value)} placeholder="e.g. customer asked to be invoiced now" /></label>
            <button type="submit" className="ledger-queue-primary" disabled={busy}>Yes, approve and send</button><button type="button" onClick={() => { setReleasing(null); setReleaseReason(''); }}>Not yet</button>
          </form> : null}
          <AdviceMoveForms item={item} moves={moves} customerName={customerName} />
        </li>)}
      </ul> : null) : <p className="ledger-queue-muted">Nothing in Advice.</p>) : null}
      {status === 'ready' && archived.length ? <div className="ledger-archive" data-testid="ledger-archive">
        <button type="button" className="ledger-archive-tag" data-testid="ledger-archive-tag" aria-expanded={archiveOpen} aria-controls="ledger-archive-list" onClick={() => setArchiveOpen((open) => !open)}>
          Rejected ({archived.length})
        </button>
        {archiveOpen ? <ul className="ledger-advice-list" id="ledger-archive-list" data-testid="ledger-archive-list">
          {archived.map((item) => <li className="ledger-advice-line" key={item.adviceId} data-testid="ledger-archive-line">
            <span className="ledger-queue-pill pill-rejected">Rejected</span>
            <span className="ledger-advice-desc">{[formatDay(item.date), item.description].filter(Boolean).join(' · ')}<TxChip handOffId={item.handOffId} orgId={orgId} customerUserId={userId} />
              <span className="ledger-queue-muted" data-testid="ledger-archive-note"> · {item.note || 'No reason recorded.'}</span></span>
            <span className="ledger-queue-money">{money(item.amount, item.currency)}</span>
            <span className="ledger-queue-actions">
              {canAct && item.canRestore ? <button type="button" className="ledger-queue-link" disabled={busy} data-testid="ledger-archive-restore" onClick={() => askReason(item, 'restore')}>Restore</button> : null}
            </span>
          </li>)}
        </ul> : null}
      </div> : null}
      {isAdviceStatementPanelEnabled() ? <AdviceStatementPanel orgId={orgId} scope="admin" targetUserId={userId} /> : null}
      </div> : null}
    </section>

    <section className="ledger-account-section ledger-queue-section" data-testid="ledger-approval-section" aria-labelledby="ledger-approval-heading">
      <h3 id="ledger-approval-heading">Approval — next invoice</h3>
      <MoveStatus moves={moves} scope="approval" />
      {issueNotice ? <p className="ledger-queue-muted" role="status" data-testid="ledger-issue-notice">{issueNotice}</p> : null}
      {issued ? <p className="ledger-invoice-issued" role="status"><strong>{issued.invoiceNumber || 'Invoice'}</strong> issued for {money(issued.total, issued.currency)}{issued.emailed === true ? ' and emailed' : ''}{issued.emailed === false ? `; the email has not been sent yet${issued.sendError ? ` (${issued.sendError})` : ''}` : ''}. It now appears in Recent transactions; corrections by credit note only.</p> : null}
      {status === 'error' ? <p className="ledger-account-error" role="alert">{loadError}</p> : null}
      <DraftGroupSwitcher drafts={drafts} value={selectedId} onChange={choose} sentence={selectorSentence} />
      {status === 'ready' ? <article className="ledger-next-invoice" aria-label={`Next invoice for ${customerName}`}>
        <div className="ledger-next-invoice-head">
          <span className="ledger-next-invoice-title">Next invoice</span><span>Draft · not yet issued</span>
          {draft?.groupLabel ? <span data-testid="ledger-draft-group">{draft.groupLabel}</span> : null}
          {draft?.billTo ? <span data-testid="ledger-draft-bill-to">Bill to: {draft.billTo}</span> : null}
          <span>To: {customerName}{organisationName ? ` · ${organisationName}` : ''}</span><span>{preview?.nextInvoiceNumber ? `Invoice number: assigned when issued (next: ${preview.nextInvoiceNumber})` : 'Number assigned when approved'}</span>
        </div>
        {approvalError ? <p className="ledger-account-error" role="alert">{approvalError}</p> : null}
        {hasLines ? <>
          <table>
            <thead><tr><th>Line</th><th className="ledger-queue-amount">Amount</th><th><span className="ledger-queue-sr">Actions</span></th></tr></thead>
            <tbody>{lines.map((line) => <tr key={line.lineId} data-testid="ledger-draft-line" className="ledger-row-open" tabIndex={0}
              onClick={(event) => openRow(event, 'draft', line.lineId, line)} onKeyDown={(event) => rowKeyDown(event, 'draft', line.lineId, line)}>
              <td>{line.description}{line.share ? <span data-testid="ledger-draft-share"> ({shareText(line.share)})</span> : null}<TxChip handOffId={line.handOffId} orgId={orgId} customerUserId={userId} />{line.audit.map((note) => <div className="ledger-line-audit" key={note}>{note}</div>)}</td>
              <td className="ledger-queue-amount ledger-queue-money">{money(line.amount, draft.currency)}</td>
              <td>{canAct ? <span className="ledger-queue-actions">
                {/* M004-F0740 (amended): a visible Edit on an editable line. It opens the SAME drawer already in edit mode: there is still one Edit form. */}
                {!isLineLocked(line.stage) ? <button type="button" className="ledger-queue-link" data-testid="ledger-draft-line-edit" disabled={busy} onClick={(event) => openRow(event, 'draft', line.lineId, line, true)}>Edit</button> : null}
                {moves.canMove(line) ? <DraftLineBackToAdvice line={line} moves={moves} disabled={busy} />
                  : <button type="button" className="ledger-queue-link" disabled={busy} onClick={() => lineRemove(line.lineId)}>{line.source === 'manual' ? 'Remove' : 'Back to advice'}</button>}
              </span> : null}</td>
            </tr>)}</tbody>
          </table>
          <dl className="ledger-invoice-totals">
            <dt>Net</dt><dd className="ledger-queue-money">{money(draft.net, draft.currency)}</dd>
            <dt>VAT</dt>
            {/* Owner ruling 2026-10-01 ("Vat is 0%"): driven by the real draft
                data (Draft.vat_registered, Draft.vat_minor — both required by
                Spec 1.1.0, never absent), never a mock placeholder. A
                non-VAT-registered org's vat_minor is 0 server-side, so this
                shows the real computed amount (£0.00) with a "(0%)" label,
                not a synthetic "Not VAT registered" string standing in for
                the number — if vat_minor were ever unexpectedly nonzero here
                that would show up as a visible inconsistency, not get hidden. */}
            <dd className="ledger-queue-money">{money(draft.vat, draft.currency)}{draft.vatRegistered === false ? ' (0%)' : ''}</dd>
            <dt className="grand">Total · {lines.length} line{lines.length === 1 ? '' : 's'}</dt><dd className="ledger-queue-money grand">{money(draft.total, draft.currency)}</dd>
          </dl>
        </> : <p className="ledger-queue-muted">No lines yet. Costs that are ready are added here by the Ledger. If something you expect is missing, check Problems in Billing.</p>}
        {canAct && adding ? <form className="ledger-queue-form" onSubmit={saveManual} noValidate>
          <label>Description<input value={manualForm.description} onChange={(event) => setManualForm({ ...manualForm, description: event.target.value })} placeholder="e.g. Late cancellation fee" /></label>
          <label>Amount (£)<input inputMode="decimal" value={manualForm.amount} onChange={(event) => setManualForm({ ...manualForm, amount: event.target.value })} /></label>
          <button type="submit" className="ledger-queue-primary" disabled={busy}>Add line</button><button type="button" onClick={closeForms}>Cancel</button>
        </form> : null}
        {canAct && confirming ? <div className="ledger-queue-form" role="alert">
          <span>Issue this invoice to {customerName} for <strong>{money(draft.total, draft.currency)}</strong>? It gets a number, is posted to the ledger and emailed, and can then only be corrected by credit note.</span>
          <button type="button" className="ledger-queue-primary" disabled={busy || issuing} onClick={approve}>{issuing ? 'Issuing…' : 'Yes, issue invoice'}</button><button type="button" onClick={closeForms}>Not yet</button>
        </div> : <div className="ledger-next-invoice-actions" data-testid="ledger-next-invoice-actions">
          {canAct ? <button type="button" className="ledger-queue-secondary" disabled={busy} onClick={() => { closeForms(); setAdding(true); }}>Add manual line</button> : null}
          <button type="button" className="ledger-queue-secondary" data-testid="ledger-preview-button" disabled={busy || previewing || !draft} aria-busy={previewing ? 'true' : undefined} onClick={openPreview}>{previewing ? 'Preparing preview…' : 'Preview (PDF)'}</button>
          {canAct ? <button type="button" className="ledger-queue-primary ledger-queue-approve" disabled={busy || issuing || !hasLines} onClick={() => { closeForms(); setConfirming(true); }}>{issuing ? 'Issuing…' : 'Approve & issue invoice'}</button> : null}
        </div>}
        <div className="ledger-preview-status" data-testid="ledger-preview-status" aria-live="polite">
          {previewing ? 'Preparing preview…' : previewMessage}
        </div>
        {previewError ? <p className="ledger-account-error" role="alert">{previewError}</p> : null}
        {preview && preview.items.length ? <ul className="ledger-preview-readiness" data-testid="ledger-preview-readiness" aria-label="Before you issue this invoice">
          {preview.items.map((item) => <li key={`${item.code}-${item.message}`} className={`ledger-preview-item severity-${item.severity}`} data-severity={item.severity}>
            <strong>{SEVERITY_LABEL[item.severity]}:</strong> {item.message}
            {BILLING_FIELD_FOR_READINESS[item.code] ? <> <Link className="ledger-queue-link" to={`/app/ledger/${orgId}/billing-settings#${BILLING_FIELD_FOR_READINESS[item.code]}`} data-testid={`ledger-readiness-fix-${item.code}`}>Fix in Billing settings</Link></> : null}
          </li>)}
        </ul> : null}
      </article> : null}
    </section>
  </>;
}
